/**
 * Proposal API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  Proposal,
  ProposalListParams,
  ProposalListResponse,
  ProposalStats,
  CreateProposalRequest,
  UpdateProposalRequest,
} from '../types/proposal';

/**
 * Proposals API Endpoints
 * Base URL: /projects/:projectId/proposals
 */
export const proposalApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ==========================================================================
    // STATS
    // ==========================================================================

    getProposalStats: builder.query<
      ApiResponse<ProposalStats>,
      { projectId: string }
    >({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/proposals/stats`,
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: 'ProposalStats', id: projectId },
      ],
    }),

    // ==========================================================================
    // LIST
    // ==========================================================================

    getProposals: builder.query<
      ApiResponse<ProposalListResponse>,
      { projectId: string; params?: ProposalListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/proposals`,
        params,
      }),
      providesTags: ['Proposal'],
    }),

    // ==========================================================================
    // GET SINGLE
    // ==========================================================================

    getProposal: builder.query<
      ApiResponse<Proposal>,
      { projectId: string; proposalId: string }
    >({
      query: ({ projectId, proposalId }) => ({
        url: `/projects/${projectId}/proposals/${proposalId}`,
      }),
      providesTags: (_result, _error, { proposalId }) => [
        { type: 'Proposal', id: proposalId },
      ],
    }),

    // ==========================================================================
    // CREATE
    // ==========================================================================

    createProposal: builder.mutation<
      ApiResponse<Proposal>,
      { projectId: string; data: CreateProposalRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/proposals`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Proposal', 'ProposalStats'],
    }),

    // ==========================================================================
    // UPDATE
    // ==========================================================================

    updateProposal: builder.mutation<
      ApiResponse<Proposal>,
      { projectId: string; proposalId: string; data: UpdateProposalRequest }
    >({
      query: ({ projectId, proposalId, data }) => ({
        url: `/projects/${projectId}/proposals/${proposalId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { proposalId }) => [
        'Proposal',
        'ProposalStats',
        { type: 'Proposal', id: proposalId },
      ],
    }),

    // ==========================================================================
    // DELETE
    // ==========================================================================

    deleteProposal: builder.mutation<
      ApiResponse<void>,
      { projectId: string; proposalId: string }
    >({
      query: ({ projectId, proposalId }) => ({
        url: `/projects/${projectId}/proposals/${proposalId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Proposal', 'ProposalStats'],
    }),

    // ==========================================================================
    // SEND
    // ==========================================================================

    sendProposal: builder.mutation<
      ApiResponse<Proposal>,
      { projectId: string; proposalId: string }
    >({
      query: ({ projectId, proposalId }) => ({
        url: `/projects/${projectId}/proposals/${proposalId}/send`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { proposalId }) => [
        'Proposal',
        'ProposalStats',
        { type: 'Proposal', id: proposalId },
      ],
    }),

    // ==========================================================================
    // REVISE
    // ==========================================================================

    reviseProposal: builder.mutation<
      ApiResponse<Proposal>,
      { projectId: string; proposalId: string }
    >({
      query: ({ projectId, proposalId }) => ({
        url: `/projects/${projectId}/proposals/${proposalId}/revise`,
        method: 'POST',
      }),
      invalidatesTags: ['Proposal', 'ProposalStats'],
    }),
  }),
});

export const {
  useGetProposalStatsQuery,
  useGetProposalsQuery,
  useGetProposalQuery,
  useCreateProposalMutation,
  useUpdateProposalMutation,
  useDeleteProposalMutation,
  useSendProposalMutation,
  useReviseProposalMutation,
} = proposalApi;
